import { useCallback, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { PrivateLayout } from "../Layout/Private/PrivateLayout";

const hostname = window.location.hostname;
const subdomain = hostname.split(".")[0];

const INACTIVE_REAUTH_MS = 15 * 60 * 1000;

let apiBaseUrl;
if (hostname.includes("localhost")) {
    apiBaseUrl = `http://localhost:8000`;
} else {
    apiBaseUrl = `https://${subdomain}.hordyplan.fr`;
}

const authClient = axios.create({
    baseURL: apiBaseUrl,
    headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json",
    },
    withCredentials: true,
    withXSRFToken: true,
});

export const ProtectedRoute = ({ user, children, setUser }) => {
    const location = useLocation();
    const isCheckingAuth = useRef(false);
    const hiddenAt = useRef(null);

    const verifyAuth = useCallback(async () => {
        if (isCheckingAuth.current) {
            return;
        }

        isCheckingAuth.current = true;

        try {
            const resp = await authClient.get("/api/user");
            setUser(resp.data);
        } catch {
            setUser(null);
            window.location.href = "/";
        } finally {
            isCheckingAuth.current = false;
        }
    }, [setUser]);

    useEffect(() => {
        verifyAuth();
    }, [verifyAuth]);

    useEffect(() => {
        if (!user && !isCheckingAuth.current) {
            verifyAuth();
        }
    }, [user, verifyAuth]);

    // Navigation interne = activité utilisateur, pas de re-vérification
    useEffect(() => {
        hiddenAt.current = null;
    }, [location.pathname]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                hiddenAt.current = Date.now();
                return;
            }

            if (hiddenAt.current === null) {
                return;
            }

            const inactiveDuration = Date.now() - hiddenAt.current;
            hiddenAt.current = null;

            if (inactiveDuration >= INACTIVE_REAUTH_MS) {
                verifyAuth();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [verifyAuth]);

    return children ? (
        children
    ) : (
        <div>
            {user && (
                <PrivateLayout user={user} setUser={setUser}>
                    <Outlet />
                </PrivateLayout>
            )}
        </div>
    );
};

export const request = ({ ...options }) => {
    const onSuccess = (response) => response;
    const onError = (error) => {

        if (error?.response?.status === 401) {
            window.location.href = "/login";
            return Promise.reject(error);
        }
        if (error?.response?.status === 419) {
            window.location.href = "/login";
            return Promise.reject(error);
        }
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
            return Promise.reject({
                type: "timeout",
                message: "Délai de requête dépassé, vérifiez votre connexion.",
            });
        }

        return Promise.reject(error);
    };

    return authClient(options).then(onSuccess).catch(onError);
};
